const URN_PREFIX = "urn:forkto:record";
const CURRENT_VERSION = "v1";
const RECORD_TYPE_PATTERN = /^[a-z][a-z0-9-]*$/;
const RECORD_ID_PATTERN = /^[A-Za-z0-9._~-]+$/;
const OBJECT_ID_PATTERN = /^[a-fA-F0-9]{24}$/;

export const UNIVERSAL_RECORD_VERSION = CURRENT_VERSION;
export const UNIVERSAL_RECORD_TYPES = Object.freeze({
    Meat: "meat",
    Ingredient: "ingredient",
    Packaging: "packaging",
    Product: "product"
});

const OBJECT_ID_RECORD_TYPES = new Set(Object.values(UNIVERSAL_RECORD_TYPES));

export class UniversalRecordIdentifierError extends Error {
    constructor(code, message, details = {}) {
        super(message);
        this.name = "UniversalRecordIdentifierError";
        this.code = code;
        this.details = details;
    }
}

function fail(code, message, details) {
    throw new UniversalRecordIdentifierError(code, message, details);
}

function validateRecordType(recordType, { normalize = false } = {}) {
    const raw = String(recordType || "").trim();
    const normalized = normalize ? raw.toLowerCase() : raw;
    if (!RECORD_TYPE_PATTERN.test(normalized)) {
        fail("invalid_record_type", "The Forkto record type is invalid.");
    }
    return normalized;
}

function normalizeRecordID(recordType, recordId) {
    const value = String(recordId || "").trim();
    if (!value || !RECORD_ID_PATTERN.test(value)) {
        fail("invalid_record_id", "The Forkto record ID is invalid.", { recordType });
    }
    if (OBJECT_ID_RECORD_TYPES.has(recordType)) {
        if (!OBJECT_ID_PATTERN.test(value)) {
            fail("invalid_record_id", "This Forkto record type requires a MongoDB ObjectID.", {
                recordType
            });
        }
        return value.toLowerCase();
    }
    return value;
}

export function buildUniversalRecordIdentifier(recordType, recordId) {
    const type = validateRecordType(recordType, { normalize: true });
    const id = normalizeRecordID(type, recordId);
    return `${URN_PREFIX}:${CURRENT_VERSION}:${type}:${id}`;
}

export function parseUniversalRecordIdentifier(value) {
    const input = String(value || "").trim();
    const parts = input.split(":");
    if (
        parts.length !== 6 ||
        parts[0] !== "urn" ||
        parts[1] !== "forkto" ||
        parts[2] !== "record"
    ) {
        fail("malformed_identifier", "This is not a Forkto universal record identifier.");
    }

    const version = parts[3];
    if (version !== CURRENT_VERSION) {
        fail(
            "unsupported_version",
            `Forkto record identifier version ${version || "(missing)"} is not supported.`,
            {
                version
            }
        );
    }

    const recordType = validateRecordType(parts[4]);
    const recordId = normalizeRecordID(recordType, parts[5]);

    return {
        version,
        recordType,
        recordId,
        urn: buildUniversalRecordIdentifier(recordType, recordId)
    };
}

const PUBLIC_TRACE_PREFIX = "https://forkto.com/trace/";

/**
 * Printed product labels (legacyLabelPdf.js) encode a public trace URL,
 * `https://forkto.com/trace/<base64("<productId>/<tenant>")>`, rather than
 * a record URN. Scanning one of those is the most common thing a user does
 * with the camera, so the identify flow accepts it and normalises it to a
 * product identifier. The tenant is carried through so the caller can refuse
 * a label that belongs to a different account.
 */
export function parsePublicTraceUrl(value) {
    const input = String(value || "").trim();
    if (!input.startsWith(PUBLIC_TRACE_PREFIX)) return null;

    let decoded = "";
    try {
        decoded = globalThis.atob(input.slice(PUBLIC_TRACE_PREFIX.length));
    } catch {
        fail("malformed_identifier", "This Forkto trace link could not be read.");
    }
    const [productId, tenant] = decoded.split("/");
    if (!OBJECT_ID_PATTERN.test(productId || "")) {
        fail("malformed_identifier", "This Forkto trace link does not contain a product ID.");
    }
    const parsed = parseUniversalRecordIdentifier(
        buildUniversalRecordIdentifier(UNIVERSAL_RECORD_TYPES.Product, productId)
    );
    return { ...parsed, labelTenant: tenant || "" };
}

export function parseUniversalRecordInput(value, { expectedType, allowBareId = false } = {}) {
    const input = String(value || "").trim();
    const fromTraceUrl = parsePublicTraceUrl(input);
    if (fromTraceUrl) return fromTraceUrl;
    if (allowBareId && OBJECT_ID_PATTERN.test(input)) {
        if (!expectedType) {
            fail(
                "record_type_required",
                "A record type is required when entering a bare record ID."
            );
        }
        return parseUniversalRecordIdentifier(buildUniversalRecordIdentifier(expectedType, input));
    }
    return parseUniversalRecordIdentifier(input);
}

export async function resolveUniversalRecord(
    parsedIdentifier,
    { resolvers = {}, ...context } = {}
) {
    if (!parsedIdentifier?.recordType || !parsedIdentifier?.recordId) {
        fail("malformed_identifier", "A parsed Forkto record identifier is required.");
    }
    const resolver = resolvers[parsedIdentifier.recordType];
    if (typeof resolver !== "function") {
        fail(
            "unsupported_record_type",
            `Forkto record type ${parsedIdentifier.recordType} is not supported here.`,
            { recordType: parsedIdentifier.recordType }
        );
    }
    return resolver(parsedIdentifier.recordId, context);
}
